import { useMemo, useState } from "react";
import { BarChart3, ShieldCheck, TerminalSquare, Users } from "lucide-react";
import { ImportedToolFrame } from "./ImportedToolFrame";

const INTERNAL_VIEWS = [
  {
    id: "usage",
    title: "Usage Analytics",
    description: "Imported usage dashboard for internal AI activity review.",
    src: "/agent-dashboard/usage/index.html",
  },
  {
    id: "logs",
    title: "Logs Explorer",
    description: "Imported observability view for tracing internal AI operations.",
    src: "/agent-dashboard/logs/index.html",
  },
  {
    id: "guardrails",
    title: "Guardrails & Policies",
    description: "Imported policy and guardrail console for internal review.",
    src: "/agent-dashboard/guardrails/index.html",
  },
  {
    id: "playground",
    title: "Prompt Playground",
    description: "Imported sandbox for internal prompt and workflow exploration.",
    src: "/agent-dashboard/playground/index.html",
  },
  {
    id: "teams",
    title: "Teams & Users",
    description: "Imported team-management surface for internal operational review.",
    src: "/agent-dashboard/teams/index.html",
  },
];

const metricCards = [
  { key: "members", label: "Member Accounts", icon: Users },
  { key: "active_members", label: "Active Subscribers", icon: ShieldCheck },
  { key: "scored_bids", label: "Bid Scores Run", icon: TerminalSquare },
];

export function AIOpsPanel({ summary }) {
  const [activeView, setActiveView] = useState("usage");
  const currentView = useMemo(() => INTERNAL_VIEWS.find((view) => view.id === activeView) || INTERNAL_VIEWS[0], [activeView]);

  return (
    <div className="space-y-6" data-testid="admin-ai-ops-panel">
      <div className="grid md:grid-cols-3 gap-4">
        {metricCards.map(({ key, label, icon: Icon }) => (
          <div key={key} className="card-dark p-5" data-testid={`admin-ai-ops-stat-${key}`}>
            <Icon className="w-5 h-5 text-[var(--red)] mb-3" />
            <div className="font-display text-2xl font-semibold">{summary?.[key] ?? 0}</div>
            <div className="text-xs text-[var(--text-3)] uppercase tracking-[0.2em] mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-dark p-6" data-testid="admin-ai-ops-recent-members">
          <div className="overline text-[var(--red)] mb-4">Recent Members</div>
          <div className="space-y-3">
            {(summary?.recent_members || []).map((member) => (
              <div key={`${member.email}-${member.created_at}`} className="flex items-center justify-between gap-4 border-b border-[var(--border)] pb-3 last:border-0 last:pb-0">
                <div>
                  <div className="text-sm font-medium text-white">{member.email}</div>
                  <div className="text-xs text-[var(--text-3)]">Created {member.created_at?.slice(0, 10) || "—"}</div>
                </div>
                <div className="text-xs uppercase tracking-wide text-[var(--silver)]">{member.subscription_status || "none"}</div>
              </div>
            ))}
            {!summary?.recent_members?.length && <div className="text-sm text-[var(--text-3)]">No member records yet.</div>}
          </div>
        </div>

        <div className="card-dark p-6" data-testid="admin-ai-ops-recent-scores">
          <div className="overline text-[var(--red)] mb-4">Recent Bid Scores</div>
          <div className="space-y-3">
            {(summary?.recent_scores || []).map((score) => (
              <div key={`${score.member_id}-${score.created_at}`} className="flex items-center justify-between gap-4 border-b border-[var(--border)] pb-3 last:border-0 last:pb-0">
                <div>
                  <div className="text-sm font-medium text-white">{score.filename || "Unnamed PDF"}</div>
                  <div className="text-xs text-[var(--text-3)]">{score.member_id} · {score.created_at?.slice(0, 10) || "—"}</div>
                </div>
                <BarChart3 className="w-4 h-4 text-[var(--silver)]" />
              </div>
            ))}
            {!summary?.recent_scores?.length && <div className="text-sm text-[var(--text-3)]">No bid-scoring activity yet.</div>}
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[var(--border)] overflow-x-auto" data-testid="admin-ai-ops-subtabs">
        {INTERNAL_VIEWS.map((view) => (
          <button
            key={view.id}
            onClick={() => setActiveView(view.id)}
            className={`px-5 py-3 text-sm font-medium transition relative whitespace-nowrap ${activeView === view.id ? "text-white" : "text-[var(--text-2)] hover:text-white"}`}
            data-testid={`admin-ai-ops-tab-${view.id}`}
          >
            {view.title}
            {activeView === view.id && <span className="absolute -bottom-px inset-x-0 h-px bg-[var(--red)]" />}
          </button>
        ))}
      </div>

      <ImportedToolFrame title={currentView.title} description={currentView.description} src={currentView.src} />
    </div>
  );
}